import React, { Component } from 'react'
import { Popover, OverlayTrigger } from 'react-bootstrap'
import Select from 'react-select'
import './_questionTools.scss'

// The tag dropdown's menu stays inside the popover: a menu portalled to <body>
// counts as an outside click and would close the popover (rootClose).
const selectStyles = {
    menu: (base) => ({ ...base, zIndex: 5, borderRadius: 10, overflow: "hidden" }),
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderRadius: 9,
        borderColor: state.isFocused ? "#4f6bed" : "#e7e9f0",
        boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(79, 107, 237, 0.18)" : "none",
        "&:hover": { borderColor: "#4f6bed" },
        fontSize: 13,
    }),
    multiValue: (base) => ({ ...base, background: "#eef1fe", borderRadius: 999 }),
    multiValueLabel: (base) => ({ ...base, color: "#3f59d6", fontWeight: 600, fontSize: 12 }),
    multiValueRemove: (base) => ({ ...base, borderRadius: 999, color: "#3f59d6", ":hover": { background: "#dfe5fd", color: "#2f47c0" } }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        background: state.isFocused ? "#eef1fe" : "#fff",
        color: "#1b2430",
    }),
};

// Report / Notes / Bookmark icons for a question, each opening a popover form.
//
// Each form keeps its own state and is mounted only while its popover is open,
// so closing and reopening always starts clean: no leftover errors, selections
// or text. Saving is done by the caller (onReport / onNote / onBookmark return a
// promise) so the parent keeps updating its question state and the tag cache.
//
// Tags: either choose from the student's own tags or create one new tag (see
// TagPicker); only the chosen option is sent.

const errorText = (err) => {
    if (err && err.graphQLErrors && err.graphQLErrors.length) return err.graphQLErrors.map((x) => x.message).join(", ");
    return (err && err.message) || "Something went wrong. Please try again.";
};

// Tags: EITHER pick from the student's own tags (searchable multi-select) OR
// create one new tag -- a switch at the top chooses which. Only the chosen
// option is sent. A "new" name that matches one of their tags uses that tag.
class TagPicker extends Component {
    render() {
        const { tags, mode, onMode, selected, onSetSelected, newTag, onNewTag, label } = this.props;
        const hasTags = tags.length > 0;
        return (
            <div className="qt-field">
                <div className="qt-label">{label}</div>
                <div className="qt-switch" role="tablist" aria-label="Tag option">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mode === "existing"}
                        className={mode === "existing" ? "qt-switch-btn is-on" : "qt-switch-btn"}
                        disabled={!hasTags}
                        title={hasTags ? "" : "You don't have any tags yet"}
                        onClick={() => onMode("existing")}>
                        Choose existing
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mode === "new"}
                        className={mode === "new" ? "qt-switch-btn is-on" : "qt-switch-btn"}
                        onClick={() => onMode("new")}>
                        Create new
                    </button>
                </div>
                {mode === "existing" ? (
                    <Select
                        isMulti
                        autoFocus
                        aria-label="Your tags"
                        placeholder="Select your tags"
                        noOptionsMessage={() => "No matching tag"}
                        options={tags.map((t) => ({ value: String(t.id), label: t.tag }))}
                        value={tags.filter((t) => selected.includes(String(t.id))).map((t) => ({ value: String(t.id), label: t.tag }))}
                        onChange={(vals) => onSetSelected((vals || []).map((v) => v.value))}
                        classNamePrefix="qt-select"
                        maxMenuHeight={180}
                        styles={selectStyles}
                    />
                ) : (
                    <>
                        <input
                            type="text"
                            className="qt-input"
                            placeholder="Type a new tag name"
                            value={newTag}
                            maxLength={40}
                            autoComplete="off"
                            autoFocus
                            onChange={(e) => onNewTag(e.target.value)}
                        />
                        {!hasTags ? <div className="qt-hint mt-2">You haven't created any tags yet.</div> : null}
                    </>
                )}
            </div>
        );
    }
}

// Existing note (optional, notes only): { tags: "id,id", comments } -- the form
// opens with its tags selected and its text filled in, for editing.
const noteIds = (note, tags) => {
    if (!note || !note.tags) return [];
    const own = new Set(tags.map((t) => String(t.id)));
    return String(note.tags).split(",").map((x) => x.trim()).filter((x) => own.has(x));
};

class ToolForm extends Component {
    state = {
        tagMode: noteIds(this.props.note, this.props.tags || []).length > 0 || (this.props.tags && this.props.tags.length > 0) ? "existing" : "new",
        selected: noteIds(this.props.note, this.props.tags || []),
        newTag: "",
        reason: "",
        comment: (this.props.note && this.props.note.comments) || "",
        error: "",
        saving: false,
        saved: false,
    };

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.closeTimer);
    }

    // Only the chosen option is sent: the selected tags, or the one new tag. A new
    // name that matches one of their tags is sent as that tag instead.
    tagPayload() {
        const { tagMode, selected, newTag } = this.state;
        if (tagMode === "existing") return { tags: selected.join(","), new_tag: "" };
        const name = newTag.trim();
        const match = this.props.tags.find((t) => String(t.tag).trim().toLowerCase() === name.toLowerCase());
        return match ? { tags: String(match.id), new_tag: "" } : { tags: "", new_tag: name };
    }

    submit = () => {
        const { kind } = this.props;
        const { tagMode, selected, newTag, reason, comment } = this.state;
        if (kind === "report") {
            if (!reason) return this.setState({ error: "Choose a reason." });
            if (!comment.trim()) return this.setState({ error: "Add a comment." });
        } else {
            if (tagMode === "existing" && selected.length === 0) return this.setState({ error: "Select at least one tag." });
            if (tagMode === "new" && !newTag.trim()) return this.setState({ error: "Type a name for the new tag." });
            if (kind === "notes" && !comment.trim()) return this.setState({ error: "Write your note." });
        }
        this.setState({ saving: true, error: "" });
        const payload = kind === "report"
            ? { report_id: parseInt(reason, 10), comments: comment.trim() }
            : { ...this.tagPayload(), ...(kind === "notes" ? { comments: comment.trim() } : {}) };
        Promise.resolve(this.props.onSave(payload))
            .then(() => {
                if (this.unmounted) return;
                this.setState({ saving: false, saved: true });
                this.closeTimer = setTimeout(() => this.props.onClose(), 900);
            })
            .catch((err) => {
                if (!this.unmounted) this.setState({ saving: false, error: errorText(err) });
            });
    };

    render() {
        const { kind, tags, reasons, onClose } = this.props;
        const { tagMode, selected, newTag, reason, comment, error, saving, saved } = this.state;
        const meta = {
            report: { title: "Report this question", icon: "fa-flag", done: "Thanks, your report was sent." },
            notes: { title: "Add a note", icon: "fa-sticky-note", done: "Note saved." },
            bookmark: { title: "Bookmark", icon: "fa-bookmark", done: "Bookmarked." },
        }[kind];

        return (
            <div className="qt-form">
                <div className="qt-head">
                    <span className={"qt-head-icon qt-head-icon--" + kind}><i className={"fas " + meta.icon} /></span>
                    <h6 className="qt-title">{meta.title}</h6>
                    <button type="button" className="qt-close" aria-label="Close" onClick={onClose}><i className="fas fa-times" /></button>
                </div>

                {saved ? (
                    <div className="qt-done"><i className="fas fa-check-circle" /> {meta.done}</div>
                ) : (
                    <>
                        <div className="qt-body">
                            {kind === "report" ? (
                                <div className="qt-field">
                                    <div className="qt-label">Reason</div>
                                    <Select
                                        aria-label="Reason"
                                        placeholder="Select a reason"
                                        isSearchable={reasons.length > 6}
                                        options={reasons.map((r) => ({ value: String(r.id), label: r.report }))}
                                        value={reasons.filter((r) => String(r.id) === String(reason)).map((r) => ({ value: String(r.id), label: r.report }))}
                                        onChange={(v) => this.setState({ reason: v ? v.value : "", error: "" })}
                                        classNamePrefix="qt-select"
                                        maxMenuHeight={180}
                                        styles={selectStyles}
                                    />
                                </div>
                            ) : (
                                <TagPicker
                                    label={kind === "notes" ? "Your note tags" : "Your bookmark tags"}
                                    tags={tags}
                                    selected={selected}
                                    mode={tagMode}
                                    onMode={(m) => this.setState({ tagMode: m, error: "" })}
                                    newTag={newTag}
                                    onSetSelected={(ids) => this.setState({ selected: ids, error: "" })}
                                    onNewTag={(name) => this.setState({ newTag: name, error: "" })}
                                />
                            )}

                            {kind !== "bookmark" ? (
                                <div className="qt-field">
                                    <div className="qt-label">{kind === "notes" ? "Note" : "Comment"}</div>
                                    <textarea
                                        className="qt-input qt-textarea"
                                        rows={3}
                                        placeholder={kind === "notes" ? "Write your note for this question" : "Tell us what's wrong"}
                                        value={comment}
                                        onChange={(e) => this.setState({ comment: e.target.value, error: "" })}
                                    />
                                </div>
                            ) : null}

                            {error ? <div className="qt-error" role="alert"><i className="fas fa-exclamation-circle" /> {error}</div> : null}
                        </div>
                        <div className="qt-foot">
                            <button type="button" className="qt-btn qt-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                            <button type="button" className="qt-btn qt-btn--primary" onClick={this.submit} disabled={saving}>
                                {saving ? "Saving..." : kind === "report" ? "Send report" : "Save"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }
}

class QuestionTools extends Component {
    // react-bootstrap 1.0.0-beta.16's OverlayTrigger keeps its own open state
    // (no controlled show/onToggle), so each popover is closed through its
    // trigger's handleHide(), as the old popovers did.
    triggers = {};

    close = (kind) => {
        const t = this.triggers[kind];
        if (t && t.handleHide) t.handleHide();
    };

    overlay(kind) {
        const { questionId, studentGlobals = {} } = this.props;
        const allTags = studentGlobals.tags || [];
        const reasons = studentGlobals.reports || [];
        const tags = allTags.filter((t) => t.type === (kind === "notes" ? "notes" : "bookmark"));
        const onSave = {
            report: (p) => this.props.onReport({ ...p, custom_content_id: questionId }),
            notes: (p) => this.props.onNote({ ...p, custom_content_id: questionId }),
            bookmark: (p) => this.props.onBookmark({ ...p, custom_content_id: questionId }),
        }[kind];
        return (
            <Popover id={"qt-popover-" + kind} className="qt-popover">
                <Popover.Content>
                    {/* key: a new question remounts the form, so nothing carries over */}
                    <ToolForm key={kind + "-" + questionId} kind={kind} tags={tags} reasons={reasons} note={kind === "notes" ? this.props.note : null} onSave={onSave} onClose={() => this.close(kind)} />
                </Popover.Content>
            </Popover>
        );
    }

    trigger(kind, icon, title, dot) {
        return (
            <OverlayTrigger
                trigger="click"
                placement="bottom"
                rootClose
                ref={(r) => { this.triggers[kind] = r; }}
                overlay={this.overlay(kind)}>
                <button type="button" className="qt-icon" title={title} aria-label={title}>
                    <i className={icon} />
                    {dot ? <span className="qt-dot" aria-hidden="true" /> : null}
                </button>
            </OverlayTrigger>
        );
    }

    render() {
        const { bookmarked, onRemoveBookmark, tools = ["report", "notes", "bookmark"] } = this.props;
        const isBookmarked = String(bookmarked) === "true";
        const hasNote = !!(this.props.note && (this.props.note.tags || this.props.note.comments));
        return (
            <ul className="qt-tools list-inline m-1 p-0">
                {tools.includes("report") ? <li className="list-inline-item">{this.trigger("report", "fal fa-info-circle", "Report")}</li> : null}
                {tools.includes("notes") ? <li className="list-inline-item">{this.trigger("notes", "fal fa-notes-medical", hasNote ? "Edit note" : "Add note", hasNote)}</li> : null}
                {tools.includes("bookmark") ? (
                    <li className="list-inline-item">
                        {isBookmarked ? (
                            <button type="button" className="qt-icon is-bookmarked" title="Remove bookmark" aria-label="Remove bookmark" onClick={onRemoveBookmark}>
                                <i className="fas fa-bookmark" />
                            </button>
                        ) : this.trigger("bookmark", "fal fa-bookmark", "Bookmark")}
                    </li>
                ) : null}
            </ul>
        );
    }
}

// Shared with the Notes / Bookmark modals (SingleNoteModal, SingleBookModal) so
// they look and behave like these popovers.
export { TagPicker, selectStyles, errorText };
export default QuestionTools;
